import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import TextInput from "../../../components/TextInput";
import TextArea from "../../../components/TextArea";
import Hoc from "../../../components/HOC";
import MuiDatePicker from "../../../components/MuiDatePicker";
import Loader from "../../../components/Loader";
import { toast } from "react-toastify";
import { createCandidate, updateCandidate, getCandidateById } from "../../../Services/recruiterapis";

const CandidateForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const [isLoading, setIsLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    firstName: "",
    lastName: "",
    candidateName: "",
    email: "",
    phone: "",
    linkedin: "",
    notes: "",
    stsCd: "A",
    technology: "",
    gender: "M",
    dob: "",
    location: "",
    isUSA: "Yes",
    nationality: "",
    passportNumber: "",
    willingToRelocate: "No",
    workType: "",
    totalItExperience: "",
    relevantExperience: "",
    recentCompany: "",
    recentCompanyLocation: "",
    visaStatus: "",
    visaExpiryDate: null,
    recentEmploymentStart: null,
    recentEmploymentEnd: null,
    highestDegree: "",
    specialization: "",
    educationStart: null,
    educationEnd: null,
    formerTcsEmployee: "No",
    baId: "",
  });

  useEffect(() => {
    if (isEditMode && id) {
      fetchCandidateDetails();
    }
  }, [id, isEditMode]);

  const fetchCandidateDetails = async () => {
    try {
      setIsLoading(true);
      const candidate = await getCandidateById(id);

      // Split candidateName into firstName and lastName if needed
      let firstName = candidate.firstName || "";
      let lastName = candidate.lastName || "";

      if (!firstName && !lastName && candidate.candidateName) {
        const nameParts = candidate.candidateName.trim().split(/\s+/);
        firstName = nameParts[0] || "";
        lastName = nameParts.slice(1).join(" ") || "";
      }

      setInitialValues({
        firstName: firstName || "",
        lastName: lastName || "",
        candidateName: candidate.candidateName || `${firstName} ${lastName}`.trim(),
        email: candidate.email || "",
        phone: candidate.phone || "",
        linkedin: candidate.linkedin || "",
        notes: candidate.notes || "",
        stsCd: candidate.stsCd || "A",
        technology: candidate.technology || "",
        gender: candidate.gender || "M",
        dob: candidate.dob || "",
        location: candidate.location || "",
        isUSA: candidate.nationality && candidate.nationality.trim().toUpperCase() === "USA" ? "Yes" : "No",
        nationality: candidate.nationality || "",
        passportNumber: candidate.passportNumber || "",
        willingToRelocate: candidate.willingToRelocate && (candidate.willingToRelocate.toLowerCase() === "yes" || candidate.willingToRelocate.toLowerCase() === "y") ? "Yes" : "No",
        workType: candidate.workType || "",
        totalItExperience: candidate.totalItExperience || "",
        relevantExperience: candidate.relevantExperience || "",
        recentCompany: candidate.recentCompany || "",
        recentCompanyLocation: candidate.recentCompanyLocation || "",
        visaStatus: candidate.visaStatus || "",
        visaExpiryDate: candidate.visaExpiryDate ? moment(candidate.visaExpiryDate) : null,
        recentEmploymentStart: candidate.recentEmploymentStart ? moment(candidate.recentEmploymentStart) : null,
        recentEmploymentEnd: candidate.recentEmploymentEnd ? moment(candidate.recentEmploymentEnd) : null,
        highestDegree: candidate.highestDegree || "",
        specialization: candidate.specialization || "",
        educationStart: candidate.educationStart ? moment(candidate.educationStart) : null,
        educationEnd: candidate.educationEnd ? moment(candidate.educationEnd) : null,
        formerTcsEmployee: candidate.formerTcsEmployee && (candidate.formerTcsEmployee.toLowerCase() === "yes" || candidate.formerTcsEmployee.toLowerCase() === "y") ? "Yes" : "No",
        baId: candidate.baId || "",
      });
    } catch (error) {
      toast.error(error?.message || "Failed to load candidate details");
      navigate("/candidates");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setIsLoading(true);

      // Build payload with all fields
      const rawPayload = {
        firstName: values.firstName?.trim() || "",
        lastName: values.lastName?.trim() || "",
        email: values.email?.trim(),
        phone: values.phone?.trim(),
        linkedin: values.linkedin?.trim() || null,
        notes: values.notes?.trim() || null,
        stsCd: values.stsCd || "A",
        technology: values.technology?.trim(),
        gender: (values.gender || "M").trim().charAt(0).toUpperCase(),
        dob: values.dob?.trim() || null,
        location: values.location?.trim() || null,
        nationality: values.isUSA === "Yes" ? "USA" : (values.nationality?.trim() || null),
        passportNumber: values.passportNumber?.trim() || null,
        willingToRelocate: values.willingToRelocate === "Yes" ? "Yes" : "No",
        workType: values.workType?.trim() || null,
        totalItExperience: values.totalItExperience?.trim() || null,
        relevantExperience: values.relevantExperience?.trim() || null,
        recentCompany: values.recentCompany?.trim() || null,
        recentCompanyLocation: values.recentCompanyLocation?.trim() || null,
        visaStatus: values.isUSA === "No" ? (values.visaStatus?.trim() ? values.visaStatus.trim().toUpperCase() : null) : null,
        visaExpiryDate: values.isUSA === "No" ? (values.visaExpiryDate ? values.visaExpiryDate.format("YYYY-MM-DD") : null) : null,
        recentEmploymentStart: values.recentEmploymentStart ? values.recentEmploymentStart.format("YYYY-MM-DD") : null,
        recentEmploymentEnd: values.recentEmploymentEnd ? values.recentEmploymentEnd.format("YYYY-MM-DD") : null,
        highestDegree: values.highestDegree?.trim() || null,
        specialization: values.specialization?.trim() || null,
        educationStart: values.educationStart ? values.educationStart.format("YYYY-MM-DD") : null,
        educationEnd: values.educationEnd ? values.educationEnd.format("YYYY") : null,
        formerTcsEmployee: values.formerTcsEmployee === "Yes" ? "Yes" : "No",
        baId: values.formerTcsEmployee === "Yes" ? (values.baId?.trim() || null) : null,
      };

      // Send only fields with values
      const payload = Object.fromEntries(
        Object.entries(rawPayload).filter(([_, val]) => {
          if (val === null || val === undefined) return false;
          if (typeof val === "string" && val.trim() === "") return false;
          return true;
        })
      );

      if (isEditMode) {
        await updateCandidate(id, payload);
        toast.success("Candidate updated successfully!");
      } else {
        await createCandidate(payload);
        toast.success("Candidate created successfully!");
      }

      navigate("/candidates");
    } catch (error) {
      toast.error(error?.message || `Failed to ${isEditMode ? "update" : "create"} candidate`);
      setSubmitting(false);
    } finally {
      setIsLoading(false);
    }
  };

  const validate = (values) => {
    const errors = {};

    if (!values.firstName || !values.firstName.trim()) {
      errors.firstName = "First name is required";
    }
    if (!values.lastName || !values.lastName.trim()) {
      errors.lastName = "Last name is required";
    }
    if (!values.email || !values.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = "Invalid email format";
    }
    if (!values.phone || !values.phone.trim()) {
      errors.phone = "Phone is required";
    }
    if(!values.location || !values.location.trim()) {
      errors.location = "Location is required";
    }
    if(!values.linkedin || !values.linkedin.trim()) {
      errors.linkedin = "LinkedIn is required";
    }
    if (!values.technology || !values.technology.trim()) {
      errors.technology = "Technology is required";
    }
    if (!values.gender) {
      errors.gender = "Gender is required";
    }
    if (!values.stsCd) {
      errors.stsCd = "Status is required";
    }
    if (!values.formerTcsEmployee) {
      errors.formerTcsEmployee = "Former TCS Employee selection is required";
    }
    if (!values.isUSA) {
      errors.isUSA = "Is US Citizen selection is required";
    }

    // if(!values.passportNumber || !values.passportNumber.trim()) {
    //   errors.passportNumber = "Passport Number is required";
    // }
    if(!values.workType || !values.workType.trim()) {
      errors.workType = "Work Type is required";
    }
    if(!values.totalItExperience || !values.totalItExperience.trim()) {
      errors.totalItExperience = "Total  Experience is required";
    }
    if(!values.highestDegree || !values.highestDegree.trim()) {
      errors.highestDegree = "Highest Degree is required";
    }
    
    if(!values.educationEnd) {
      errors.educationEnd = "Education End is required";
    }
    // Validate fields if not USA
    if (values.isUSA === "No") {
      if (!values.nationality || !values.nationality.trim()) {
        errors.nationality = "Nationality is required when not USA";
      }
      if (!values.visaStatus || !values.visaStatus.trim()) {
        errors.visaStatus = "Visa status is required when not USA";
      }
    }

    // Validate date ranges
    if (values.recentEmploymentStart && values.recentEmploymentEnd) {
      if (values.recentEmploymentEnd.isBefore(values.recentEmploymentStart)) {
        errors.recentEmploymentEnd = "End date cannot be before start date";
      }
    }

    if (values.educationStart && values.educationEnd) {
      if (values.educationEnd.isBefore(values.educationStart)) {
        errors.educationEnd = "End date cannot be before start date";
      }
    }

    // Validate BA ID if former TCS employee is Yes
    if (values.formerTcsEmployee === "Yes") {
      if (!values.baId || !values.baId.trim()) {
        errors.baId = "BA ID is required when former TCS employee is Yes";
      }
    }

    return errors;
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <Loader isLoading={isLoading} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5">
        <div className="bg-white rounded-lg shadow-md p-2 sm:p-5">
          <div className="flex justify-end mb-2">
            <button
              onClick={() => navigate("/candidates")}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
          </div>

          <Formik
            initialValues={initialValues}
            enableReinitialize
            validate={validate}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, isSubmitting, setFieldValue }) => (
              <Form className="space-y-3">
                {/* Personal Information Section */}
                <div className="border-b border-gray-200 pb-2">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">Personal Information</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.firstName}
                        name="firstName"
                        placeholder="First Name "
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="First Name"
                        required
                      />
                      {errors.firstName && touched.firstName && (
                        <div className="text-red-500 text-sm mt-1">{errors.firstName}</div>
                      )}
                    </div>
                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.lastName}
                        name="lastName"
                        placeholder="Last Name "
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="Last Name"
                        required
                      />
                      {errors.lastName && touched.lastName && (
                        <div className="text-red-500 text-sm mt-1">{errors.lastName}</div>
                      )}
                    </div>
                    <div className="sm:col-span-1">



                       <TextInput
                        value={values.dob}
                        name="dob"
                        placeholder="Date of Birth (MM/DD)"
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="Date of Birth"
                      />
                      
                    </div>
                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.phone}
                        name="phone"
                        placeholder="Phone "
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="Phone"
                        required
                      />
                      {errors.phone && touched.phone && (
                        <div className="text-red-500 text-sm mt-1">{errors.phone}</div>
                      )}
                    </div>
                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.linkedin}
                        name="linkedin"
                        placeholder="LinkedIn URL "
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="LinkedIn"
                        required
                      />
                      {errors.linkedin && touched.linkedin && (
                        <div className="text-red-500 text-sm mt-1">{errors.linkedin}</div>
                      )}
                    </div>
                    <div className="sm:col-span-1">
                    <TextInput
                        value={values.email}
                        name="email"
                        placeholder="Email "
                        handleChange={handleChange}
                        type="email"
                        classes="w-full"
                        legend="Email"
                        required
                      />
                      {errors.email && touched.email && (
                        <div className="text-red-500 text-sm mt-1">{errors.email}</div>
                      )}                     
                    </div>


                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.location}
                        name="location"
                        placeholder="Location "
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="Location"
                        required
                      />
                      {errors.location && touched.location && (
                        <div className="text-red-500 text-sm mt-1">{errors.location}</div>
                      )}
                    </div>
                    <div className="sm:col-span-1">
                      <div className="flex items-center space-x-6 mt-1">
                        <label className="text-sm font-medium text-gray-700">Gender </label>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2">
                            <Field
                              type="radio"
                              name="gender"
                              value="M"
                              checked={values.gender === "M"}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Male</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <Field
                              type="radio"
                              name="gender"
                              value="F"
                              checked={values.gender === "F"}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Female</span>
                          </label>
                        </div>
                      </div>
                      {errors.gender && touched.gender && (
                        <div className="text-red-500 text-sm mt-1">{errors.gender}</div>
                      )}
                    </div>
                    <div className="sm:col-span-1">
                      <div className="flex items-center space-x-6 mt-1">
                        <label className="text-sm font-medium text-gray-700">Status </label>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2">
                            <Field
                              type="radio"
                              name="stsCd"
                              value="A"
                              checked={values.stsCd === "A"}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Active</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <Field
                              type="radio"
                              name="stsCd"
                              value="D"
                              checked={values.stsCd === "D"}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Inactive</span>
                          </label>
                        </div>
                      </div>
                      {errors.stsCd && touched.stsCd && (
                        <div className="text-red-500 text-sm mt-1">{errors.stsCd}</div>
                      )}
                    </div>
                    
                  </div>
                </div>

                {/* Location & Immigration Section */}
                <div className="border-b border-gray-200 pb-2">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">Work Authorization</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

                    <div className="sm:col-span-1">
                      <div className="flex items-center space-x-6 mt-1">
                        <label className="text-sm font-medium text-gray-700">
                          Is US Citizen? *
                        </label>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2">
                            <Field
                              type="radio"
                              name="isUSA"
                              value="Yes"
                              checked={values.isUSA === "Yes"}
                              onChange={(e) => {
                                setFieldValue("isUSA", "Yes");
                                setFieldValue("nationality", "");
                                setFieldValue("visaStatus", "");
                                setFieldValue("visaExpiryDate", null);
                              }}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Yes</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <Field
                              type="radio"
                              name="isUSA"
                              value="No"
                              checked={values.isUSA === "No"}
                              onChange={(e) => {
                                setFieldValue("isUSA", "No");
                              }}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">No</span>
                          </label>
                        </div>
                      </div>
                      {errors.isUSA && touched.isUSA && (
                        <div className="text-red-500 text-sm mt-1">{errors.isUSA}</div>
                      )}
                    </div>
                    {values.isUSA === "No" && (
                      <>
                        <div className="sm:col-span-1">
                          <TextInput
                            value={values.nationality}
                            name="nationality"
                            placeholder="Nationality  "
                            handleChange={handleChange}
                            type="text"
                            classes="w-full"
                            legend="Nationality "
                            required
                          />
                          {errors.nationality && touched.nationality && (
                            <div className="text-red-500 text-sm mt-1">{errors.nationality}</div>
                          )}
                        </div>
                        <div className="sm:col-span-1">
                          <TextInput
                            value={values.passportNumber} 
                            name="passportNumber"
                            placeholder="Passport Number"
                            handleChange={handleChange}
                            type="text"
                            classes="w-full"
                            legend="Passport Number"
                            required
                          />
                          {errors.passportNumber && touched.passportNumber && (
                            <div className="text-red-500 text-sm mt-1">{errors.passportNumber}</div>
                          )}
                        </div>
                        <div className="sm:col-span-1">
                          <TextInput
                            value={values.visaStatus}
                            name="visaStatus"
                            placeholder="Visa Type (e.g., GC, H1, OPT) "
                            handleChange={handleChange}
                            type="text"
                            classes="w-full"
                            legend="Visa Type"
                            required
                          />
                          {errors.visaStatus && touched.visaStatus && (
                            <div className="text-red-500 text-sm mt-1">{errors.visaStatus}</div>
                          )}
                        </div>
                        <div className="sm:col-span-1">
                          <MuiDatePicker
                            label="Visa Expiry Date"
                            value={values.visaExpiryDate}
                            onChange={(date) => setFieldValue("visaExpiryDate", date)}
                          />
                        </div>
                      </>
                    )}
                    {/* {values.isUSA === "Yes" && (
                      <div className="sm:col-span-1">
                        <TextInput
                          value={values.passportNumber}
                          name="passportNumber"
                          placeholder="Passport Number *"
                          handleChange={handleChange}
                          type="text"
                          classes="w-full"
                          legend="Passport Number"
                          required
                        />
                        {errors.passportNumber && touched.passportNumber && (
                          <div className="text-red-500 text-sm mt-1">{errors.passportNumber}</div>
                        )}
                      </div>
                    )} */}
                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.workType}
                        name="workType"
                        placeholder="Work Authorization "
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="Work Type"
                        required
                      />
                      {errors.workType && touched.workType && (
                        <div className="text-red-500 text-sm mt-1">{errors.workType}</div>
                      )}
                    </div>
                    <div className="sm:col-span-1">
                      <div className="flex items-center space-x-6 mt-1">
                        <label className="text-sm font-medium text-gray-700">
                          Willing to Relocate *
                        </label>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2">
                            <Field
                              type="radio"
                              name="willingToRelocate"
                              value="Yes"
                              checked={values.willingToRelocate === "Yes"}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Yes</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <Field
                              type="radio"
                              name="willingToRelocate"
                              value="No"
                              checked={values.willingToRelocate === "No"}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">No</span>
                          </label>
                        </div>
                      </div>
                      {errors.willingToRelocate && touched.willingToRelocate && (
                        <div className="text-red-500 text-sm mt-1">{errors.willingToRelocate}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Professional Information Section */}
                <div className="border-b border-gray-200 pb-2">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">Professional Information</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.technology}
                        name="technology"
                        placeholder="Technology "
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="Technology"
                        required
                      />
                      {errors.technology && touched.technology && (
                        <div className="text-red-500 text-sm mt-1">{errors.technology}</div>
                      )}
                    </div>
                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.totalItExperience}
                        name="totalItExperience"
                        placeholder="Total  Experience "
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="Total  Experience"
                        required
                      />
                      {errors.totalItExperience && touched.totalItExperience && (
                        <div className="text-red-500 text-sm mt-1">{errors.totalItExperience}</div>
                      )}
                    </div>
                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.relevantExperience}
                        name="relevantExperience"
                        placeholder="Relevant Experience"
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="Relevant Experience"
                      />
                    </div>

                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.recentCompany}
                        name="recentCompany"
                        placeholder="Recent Company"
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="Recent Company"
                      />
                    </div>
                    <div className="sm:col-span-1">
                      <MuiDatePicker
                        label="Recent Employment Start"
                        value={values.recentEmploymentStart}
                        onChange={(date) => {
                          setFieldValue("recentEmploymentStart", date);
                          if (values.recentEmploymentEnd && date && date.isAfter(values.recentEmploymentEnd)) {
                            setFieldValue("recentEmploymentEnd", null);
                          }
                        }}
                        maxDate={values.recentEmploymentEnd || null}
                      />
                    </div>
                    <div className="sm:col-span-1">
                      <MuiDatePicker
                        label="Recent Employment End"
                        value={values.recentEmploymentEnd}
                        onChange={(date) => setFieldValue("recentEmploymentEnd", date)}
                        minDate={values.recentEmploymentStart || null}
                      />
                      {errors.recentEmploymentEnd && touched.recentEmploymentEnd && (
                        <div className="text-red-500 text-sm mt-1">{errors.recentEmploymentEnd}</div>
                      )}
                    </div>

                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.recentCompanyLocation}
                        name="recentCompanyLocation"
                        placeholder="Recent Company Location"
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="Recent Company Location"
                      />
                    </div>
                    <div className="sm:col-span-1">
                      <div className="flex items-center space-x-6 mt-1">
                        <label className="text-sm font-medium text-gray-700">
                          Former TCS Employee *
                        </label>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2">
                            <Field
                              type="radio"
                              name="formerTcsEmployee"
                              value="Yes"
                              checked={values.formerTcsEmployee === "Yes"}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Yes</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <Field
                              type="radio"
                              name="formerTcsEmployee"
                              value="No"
                              checked={values.formerTcsEmployee === "No"}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">No</span>
                          </label>
                        </div>
                      </div>
                      {errors.formerTcsEmployee && touched.formerTcsEmployee && (
                        <div className="text-red-500 text-sm mt-1">{errors.formerTcsEmployee}</div>
                      )}
                    </div>
                    {values.formerTcsEmployee === "Yes" && (
                      <div className="sm:col-span-1">
                        <TextInput
                          value={values.baId}
                          name="baId"
                          placeholder="BA ID"
                          handleChange={handleChange}
                          type="text"
                          classes="w-full"
                          legend="BA ID *"
                        />
                        {errors.baId && touched.baId && (
                          <div className="text-red-500 text-sm mt-1">{errors.baId}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Education Section */}
                <div className="border-b border-gray-200 pb-2">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">Education</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.highestDegree}
                        name="highestDegree"
                        placeholder="Highest Degree "
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="Highest Degree"
                        required
                      />
                      {errors.highestDegree && touched.highestDegree && (
                        <div className="text-red-500 text-sm mt-1">{errors.highestDegree}</div>
                      )}
                    </div>
                    <div className="sm:col-span-1">
                      <TextInput
                        value={values.specialization}
                        name="specialization"
                        placeholder="Specialization "
                        handleChange={handleChange}
                        type="text"
                        classes="w-full"
                        legend="Specialization"
                        required
                      />
                    </div>
                    {/* <div className="sm:col-span-1">
                      <MuiDatePicker
                        label="Education Start"
                        value={values.educationStart}
                        onChange={(date) => {
                          setFieldValue("educationStart", date);
                          if (values.educationEnd && date && date.isAfter(values.educationEnd)) {
                            setFieldValue("educationEnd", null);
                          }
                        }}
                        maxDate={values.educationEnd || null}
                      />
                    </div> */}
                    <div className="sm:col-span-1">
                      <MuiDatePicker
                        label="Graduation Year "
                        value={values.educationEnd}
                        onChange={(date) => setFieldValue("educationEnd", date)}
                        views={["year"]}
                        openTo="year"
                        format="YYYY"
                        required
                      />
                      {errors.educationEnd && touched.educationEnd && (
                        <div className="text-red-500 text-sm mt-1">{errors.educationEnd}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="pb-0">
                  <h2 className="text-lg font-semibold text-gray-800 mb-1">Additional Information</h2>
                  <div className="sm:col-span-2 col-span-2">
                    <TextArea
                      value={values.notes}
                      name="notes"
                      placeholder="Notes"
                      handleChange={handleChange}
                      classes="w-full"
                      legend="Notes"
                      rows={3}
                    />
                    <div className="text-xs text-gray-500 text-right mt-0.5">
                      {(values.notes || "").length}/500 characters
                    </div>
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate("/candidates")}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                  >
                    {isSubmitting
                      ? isEditMode
                        ? "Updating..."
                        : "Creating..."
                      : isEditMode
                        ? "Update Candidate"
                        : "Create Candidate"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default Hoc(CandidateForm);
